import React, { useState, useEffect } from 'react';
import {
    Grid,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Button,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    Box,
    CircularProgress,
    TableSortLabel,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { makeStyles } from '@material-ui/core/styles';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import bulkUploadIcon from '../../icons/bulkUpload.png';
import LocationsService from '../../services/LocationsService';
import notify from '../../utils/notifier';
import PaginationComponent from '../PaginationComponent';
import BulkUploadPanel from './BulkUploadPanel';
import { hasPrivilege } from '../../utils/privileges_utility';
import { LOCATION_PRIVILEGES } from '../../utils/constants';
import { useNavigate } from 'react-router-dom';
import DescriptionOutlinedIcon from '@material-ui/icons/DescriptionOutlined';

const STATUS_COLORS = {
    'Completed': '#4caf50', // Green
    'In Review': '#f5a623', // Amber
    'New': '#6b6b6b' // Gray
};

const STATUS_BORDER_COLORS = {
    'Completed': '#5FB25C',
    'In Review': '#FEF1D9',
    'New': '#0080AF'
}

const STATUS_BACKGROUND_COLORS = {
    'Completed': '#E6F4EA',
    'In Review': '#FFF4E0',
    'New': '#E3F2FD'
};

const TYPE_COLORS = {
    'Normal': '#4caf50', // Green
    'Emergency': '#f44336' // Red
};

const useStyles = makeStyles((theme) => ({
    filterControl: {
        '& .MuiOutlinedInput-root': {
            borderRadius: 4,
            height: '32px',
            '& fieldset': {
                borderColor: '#C6C6C6'
            },
            '&:hover fieldset': {
                borderColor: '#9E9E9E'
            },
            '&.Mui-focused fieldset': {
                borderWidth: 2,
                borderColor: '#006DB3'
            }
        },
        '& .MuiSelect-select': {
            padding: '6px 16px',
            display: 'flex',
            alignItems: 'center'
        },
        '& .MuiInputLabel-outlined': {
            color: '#6F6F6F',
            fontWeight: 500,
            transform: 'translate(14px, 8px) scale(1)'
        },
        '& .MuiInputLabel-outlined.MuiInputLabel-shrink': {
            transform: 'translate(14px, -6px) scale(0.75)'
        },
        '& .MuiInputLabel-outlined.Mui-focused': {
            color: '#006DB3'
        }
    },
    pageContainer: {
        backgroundColor: '#F2F2F2',
        padding: '32px 24px 32px 24px',
        minHeight: '100%',
        display: 'flex',
        flexDirection: 'column',
        fontFamily: 'Montserrat',
        '& *': {
            fontFamily: 'Montserrat !important'
        },
        '& .MuiTypography-root, & .MuiButton-root, & .MuiInputBase-root, & .MuiInputLabel-root, & .MuiSelect-root, & .MuiMenuItem-root, & .MuiTableCell-root, & .MuiChip-root': {
            fontFamily: 'Montserrat !important'
        }
    },
    typeDialogPaper: {
        borderRadius: '4px',
        padding: 0,
        fontFamily: 'Montserrat',
        width: '640px',
        maxWidth: '640px',
        '& *': {
            fontFamily: 'Montserrat !important'
        },
        '& .MuiTypography-root, & .MuiButton-root, & .MuiInputBase-root, & .MuiInputLabel-root, & .MuiSelect-root, & .MuiMenuItem-root': {
            fontFamily: 'Montserrat !important'
        }
    },
    typeDialogTitle: {
        color: '#0F0F19',
        fontSize: '20px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        minHeight: '60px',
        height: '60px',
        padding: '0px 24px',
        '& .MuiTypography-h6': {
            fontWeight: '700 !important',
            fontSize: '20px',
            color: '#0F0F19'
        }
    },
    typeDialogContent: {
        color: '#4F5B67',
        marginBottom: '16px'
    },
    typeDialogContentContainer: {
        borderTop: '1px solid #99CCDF',
        borderBottom: '1px solid #99CCDF',
        backgroundColor: '#F9FCFF',
        padding: '24px 24px 24px 24px',
        minHeight: '220px'
    },
    typeDialogButton: {
        textTransform: 'none'
    },
    typeDialogConfirmButton: {
        textTransform: 'none',
        fontWeight: 600,
        minWidth: '62px',
        height: '36px',
        borderRadius: '100px',
        padding: '10px 16px',
        gap: '8px',
        fontSize: '14px',
        lineHeight: '16px',
        backgroundColor: '#00445D',
        color: '#FFFFFF',
        '&:hover': {
            backgroundColor: '#00445D'
        },
        '&.Mui-disabled': {
            backgroundColor: '#B6B6B9',
            color: '#FFFFFF'
        }
    },
    typeDialogActions: {
        minHeight: '60px',
        height: '60px',
        padding: '0px 24px',
        justifyContent: 'flex-end'
    },
    pageTitle: {
        fontWeight: 700,
        color: '#0F0F19',
        fontSize: '32px',
        lineHeight: '32px'
    },
    headerFlexContainer: {
        gap: '12px',
        marginTop: '4px'
    },
    headerSubtext: {
        color: '#4F5B67'
    },
    changeTypeButton: {
        textTransform: 'none',
        padding: 0
    },
    bulkUploadButton: {
        fontWeight: 600,
        borderRadius: '100px',
        backgroundColor: '#00445D',
        height: '36px',
        padding: '8px 16px',
        textTransform: 'none',
        color: '#FFFFFF',
        border: '2px solid transparent',
        transition: 'all 0.3s ease',
        '&:hover': {
            backgroundColor: '#0080AF',
            border: '2px solid transparent'
        },
        '&:focus': {
            backgroundColor: '#00445D',
            border: '2px solid #FFFFFF',
            outline: 'none'
        },
        '&:disabled': {
            backgroundColor: '#B6B6B9',
            color: '#FFFFFF',
            opacity: 0.6
        },
        '&:active, &.Mui-selected': {
            backgroundColor: '#0E6A8C'
        }
    },
    bulkUploadIcon: {
        width: '20px',
        height: '20px',
        marginRight: '8px'
    },
    filterBar: {
        padding: '16px',
        backgroundColor: '#fff',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        borderBottom: '1px solid #E0E0E0'
    },
    filterGridContainer: {
        display: 'flex',
        gap: '12px',
        alignItems: 'center',
        flex: 1
    },
    filterItem: {
        minWidth: '220px',
        flexShrink: 0
    },
    filterButtonContainer: {
        marginLeft: '8px',
        gap: '12px',
        alignItems: 'center'
    },
    clearAllButton: {
        padding: '2px 8px',
        fontSize: '0.9rem',
        minHeight: '36px',
        height: '36px',
        letterSpacing: '0.5px',
        lineHeight: '1.2',
        color: '#0E6A8C',
        fontWeight: 900,
        textTransform: 'none',
        minWidth: 'auto',
        borderRadius: '10px'
    },
    applyButton: {
        padding: '2px 16px',
        fontSize: '0.9rem',
        fontWeight: 900,
        minHeight: '36px',
        height: '36px',
        letterSpacing: '0.5px',
        lineHeight: '1.2',
        backgroundColor: '#fff',
        color: '#0E6A8C',
        borderColor: '#0E6A8C',
        borderRadius: '100px',
        textTransform: 'none',
        minWidth: 'auto'
    },
    tablePaper: {
        backgroundColor: '#fff',
        flex: 1,
        display: 'flex',
        flexDirection: 'column'
    },
    tableContainer: {
        overflowX: 'auto'
    },
    tableHeaderCell: {
        fontWeight: 700,
        fontSize: '14px'
    },
    sortIcon: {
        fontSize: '1.25rem',
        color: '#6F6F6F'
    },
    sortIconActive: {
        color: '#006DB3'
    },
    fileNameLink: {
        fontWeight: 400,
        color: '#0F0F19'
    },
    emptyStateContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '80px 20px',
        minHeight: '400px'
    },
    emptyStateIcon: {
        width: '80px !important',
        height: '80px !important',
        marginBottom: '24px',
        color: '#C6C6C6',
        fontSize: '80px'
    },
    emptyStateText: {
        fontSize: '16px',
        fontWeight: 500,
        color: '#0F0F19',
        textAlign: 'center'
    },
    selectControl: {
        backgroundColor: 'white'
    },
    dialogFormControl: {
        '& .MuiOutlinedInput-root': {
            backgroundColor: '#FFFFFF !important',
            borderRadius: '8px !important',
            minHeight: '56px',
            '& fieldset': {
                borderColor: '#BDBDBD !important',
                borderWidth: '1.5px !important',
                borderRadius: '8px !important'
            },
            '&:hover fieldset': {
                borderColor: '#757575 !important',
                borderWidth: '1.5px !important'
            },
            '&.Mui-focused fieldset': {
                borderColor: '#006DB3 !important',
                borderWidth: '2px !important'
            }
        },
        '& .MuiInputLabel-outlined': {
            color: '#757575 !important',
            fontWeight: '400 !important',
            fontSize: '16px !important',
            transform: 'translate(14px, 16px) scale(1) !important',
            '&.MuiInputLabel-shrink': {
                transform: 'translate(14px, -9px) scale(0.75) !important',
                backgroundColor: '#FFFFFF',
                padding: '0 4px'
            }
        },
        '& .MuiInputLabel-outlined.Mui-focused': {
            color: '#006DB3 !important'
        },
        '& .MuiSelect-select': {
            padding: '16px 14px !important',
            fontSize: '16px !important',
            color: '#212121 !important',
            display: 'flex !important',
            alignItems: 'center !important'
        },
        '& .MuiSelect-icon': {
            color: '#757575 !important',
            right: '12px !important'
        }
    },
    dropdownMenu: {
        '& .MuiPaper-root': {
            maxHeight: '168px',
            fontFamily: 'Montserrat !important',
            marginTop: '4px',
            boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)'
        },
        '& .MuiMenuItem-root': {
            fontFamily: 'Montserrat !important',
            fontSize: '16px',
            color: '#212121',
            padding: '12px 16px',
            minHeight: '48px'
        }
    }

}));

const UPLOAD_TYPE_OPTIONS = [
    { value: 'location', label: 'Locations' },
    { value: 'city', label: 'Cities' },
    { value: 'state', label: 'States' },
    { value: 'unlocode', label: 'Unlocodes' }
];

const UPLOAD_TYPE_LABELS = UPLOAD_TYPE_OPTIONS.reduce((acc, option) => {
    acc[option.value] = option.label;
    return acc;
}, {});

const BulkUploadTypeDialog = ({ open, initialType, initialNamespace, onClose, onConfirm }) => {
    const classes = useStyles();
    const [value, setValue] = useState(initialType);
    const [namespace, setNamespace] = useState(initialNamespace || '');
    const [namespaceOptions, setNamespaceOptions] = useState([]);

    useEffect(() => {
        if (open) {
            setValue(initialType);
            const storedNamespaces = (JSON.parse(localStorage.getItem('_applicationNamespaces') || '[]') || [])
                .filter(ns => ns && ns.toLowerCase() !== 'default');
            setNamespaceOptions(storedNamespaces);
            if (storedNamespaces.length === 0) {
                setNamespace('');
            } else if (initialNamespace && storedNamespaces.includes(initialNamespace)) {
                setNamespace(initialNamespace);
            } else {
                setNamespace(storedNamespaces[0]);
            }
        }
    }, [open, initialType, initialNamespace]);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                className: classes.typeDialogPaper
            }}
        >
            <DialogTitle className={classes.typeDialogTitle} disableTypography>
                <Typography variant="h6" style={{ fontWeight: 700, fontSize: '20px', margin: 0, lineHeight: 'normal' }}>
                    Bulk Upload
                </Typography>
                <IconButton onClick={onClose} size="small" style={{ padding: '4px' }}>
                    <CloseIcon style={{ fontSize: '20px' }} />
                </IconButton>
            </DialogTitle>
            <DialogContent className={classes.typeDialogContentContainer}>
                <FormControl variant="outlined" fullWidth className={classes.dialogFormControl}>
                    <InputLabel id="bulk-upload-type-label">File Type</InputLabel>
                    <Select
                        labelId="bulk-upload-type-label"
                        label="File Type"
                        value={value}
                        onChange={(event) => setValue(event.target.value)}
                        MenuProps={{
                            className: classes.dropdownMenu,
                            PaperProps: {
                                style: {
                                    maxHeight: 168,
                                    fontFamily: 'Montserrat'
                                }
                            },
                            anchorOrigin: {
                                vertical: 'bottom',
                                horizontal: 'left'
                            },
                            transformOrigin: {
                                vertical: 'top',
                                horizontal: 'left'
                            },
                            getContentAnchorEl: null
                        }}
                    >
                        {UPLOAD_TYPE_OPTIONS.map(option => (
                            <MenuItem key={option.value} value={option.value}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                {namespaceOptions.length > 0 && (
                    <FormControl variant="outlined" fullWidth className={classes.dialogFormControl} style={{ marginTop: '24px' }}>
                        <InputLabel id="bulk-upload-namespace-label">Namespace</InputLabel>
                        <Select
                            labelId="bulk-upload-namespace-label"
                            label="Namespace"
                            value={namespace}
                            onChange={(event) => setNamespace(event.target.value)}
                            MenuProps={{
                                className: classes.dropdownMenu,
                                PaperProps: {
                                    style: {
                                        maxHeight: 168,
                                        fontFamily: 'Montserrat'
                                    }
                                },
                                anchorOrigin: {
                                    vertical: 'bottom',
                                    horizontal: 'left'
                                },
                                transformOrigin: {
                                    vertical: 'top',
                                    horizontal: 'left'
                                },
                                getContentAnchorEl: null
                            }}
                        >
                            {namespaceOptions.map(option => (
                                <MenuItem key={option} value={option}>
                                    {option}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}
            </DialogContent>
            <DialogActions className={classes.typeDialogActions}>
                <Button
                    onClick={() => onConfirm({ type: value, namespace })}
                    variant="contained"
                    color="primary"
                    disabled={
                        !value ||
                        (namespaceOptions.length > 0 && !namespace)
                    }
                    className={classes.typeDialogConfirmButton}
                >
                    Next
                </Button>
            </DialogActions>
        </Dialog>
    );
};

const BulkUploadManagementPage = () => {
    const navigate = useNavigate();
    const classes = useStyles();
    const [uploads, setUploads] = useState([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [queryCount, setQueryCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [uploadPanelOpen, setUploadPanelOpen] = useState(false);
    const [typeSelectionOpen, setTypeSelectionOpen] = useState(false);
    const [selectedUploadType, setSelectedUploadType] = useState('location');
    const [selectedNamespace, setSelectedNamespace] = useState('');

    const initialFilterState = {
        status: '',
        type: '',
        namespace: ''
    };

    // Filter states
    const [filters, setFilters] = useState(initialFilterState);
    const [appliedFilters, setAppliedFilters] = useState(initialFilterState);

    const STATIC_STATUS_OPTIONS = ['New', 'In Review', 'Completed'];
    const STATIC_TYPE_OPTIONS = ['Normal', 'Emergency'];

    // Filter options
    const [filterOptions, setFilterOptions] = useState({
        statuses: STATIC_STATUS_OPTIONS,
        types: [],
        namespaces: []
    });

    // Sorting
    const [orderBy, setOrderBy] = useState('uploadedDate');
    const [order, setOrder] = useState('desc');

    // Check privileges on mount
    useEffect(() => {
        if (!hasPrivilege(LOCATION_PRIVILEGES['BULK_UPLOAD'])) {
            navigate('/unauthorized');
            return;
        }

        loadFilterOptions();
        fetchUploads();
    }, []);

    // Fetch data when pagination, applied filters, or sorting changes
    useEffect(() => {
        fetchUploads();
    }, [page, rowsPerPage, appliedFilters, orderBy, order]);

    const loadFilterOptions = async () => {
        const namespacesFromStorage = (JSON.parse(localStorage.getItem('_applicationNamespaces') || '[]') || [])
            .filter(namespace => namespace && namespace.toLowerCase() !== 'default');

        setFilterOptions({
            statuses: STATIC_STATUS_OPTIONS,
            types: STATIC_TYPE_OPTIONS,
            namespaces: namespacesFromStorage
        });
    };

    const fetchUploads = async () => {
        setLoading(true);
        try {
            const query = {
                _page: page + 1,
                _limit: rowsPerPage,
                _sort: orderBy,
                _order: order
            };

            // Add filters to query
            // Map "New" back to "Pending" for API call
            if (appliedFilters.status) {
                query.status = appliedFilters.status;
            }
            if (appliedFilters.type) query.type = appliedFilters.type;
            if (appliedFilters.namespace) query.namespace = appliedFilters.namespace;

            const response = await LocationsService.getBulkUploads(query);
            setUploads(response.data || []);

            // Get total count from headers (axios lowercases header names)
            const totalCount = response.headers?.['x-total-count'] ||
                response.headers?.['X-Total-Count'] ||
                response.data?.length || 0;
            setQueryCount(parseInt(totalCount));
        } catch (error) {
            console.error('Error fetching uploads:', error);
            notify({ type: 'error', message: 'Failed to load bulk uploads' });
            setUploads([]);
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (filterName, value) => {
        setFilters(prev => ({
            ...prev,
            [filterName]: value
        }));
        setPage(0); // Reset to first page when filter changes
    };

    const handleClearFilters = () => {
        setFilters(initialFilterState);
        setAppliedFilters(initialFilterState);
        setPage(0);
    };

    const handleApplyFilters = () => {
        setPage(0);
        setAppliedFilters(filters);
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
        setPage(0);
    };

    const handleUploadSuccess = () => {
        fetchUploads(); // Refresh the list after successful upload
    };

    const handleOpenTypeSelection = () => {
        setTypeSelectionOpen(true);
    };

    const handleCloseTypeSelection = () => {
        setTypeSelectionOpen(false);
    };

    const handleConfirmTypeSelection = ({ type, namespace }) => {
        const resolvedType = type || 'location';
        setSelectedUploadType(resolvedType);
        if (namespace !== undefined) {
            setSelectedNamespace(namespace);
        }
        setTypeSelectionOpen(false);
        setUploadPanelOpen(true);
    };

    const handleModalTypeChange = (type) => {
        if (type) {
            setSelectedUploadType(type);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');
        return `${day}/${month}/${year}, ${hours}:${minutes}:${seconds}`;
    };

    const mapStatusToDisplay = (status) => {
        if (status === null || status === undefined || status === '') {
            return 'New';
        }
        const normalized = String(status).toLowerCase();
        if (normalized === 'pending') {
            return 'In Review';
        }
        if (normalized === 'success' || normalized === 'failed' || normalized === 'complete' || normalized === 'completed') {
            return 'Completed';
        }
        return status;
    };

    const getStatusChip = (status) => {
        const displayStatus = mapStatusToDisplay(status);
        const color = STATUS_COLORS[displayStatus] || '#757575';
        const borderColor = STATUS_BORDER_COLORS[displayStatus];
        const backgroundColor = STATUS_BACKGROUND_COLORS[displayStatus] || '#f5f5f5';
        return (
            <Chip
                label={displayStatus}
                style={{
                    backgroundColor,
                    fontWeight: 400,
                    lineHeight: '10px',
                    fontSize: '0.75rem',
                    height: '24px',
                    color: '#000',
                    border: `1px solid ${color}`,
                    borderRadius: '6px'
                }}
                size="small"
            />
        );
    };

    const getTypeChip = (type) => {
        const color = TYPE_COLORS[type] || '#757575';
        return (
            <Chip
                label={type}
                style={{
                    backgroundColor: 'transparent',
                    color: '#000',
                    fontWeight: 300,
                    fontSize: '0.75rem',
                    height: '24px',
                    border: `1px solid ${color}`,
                    borderRadius: '6px'
                }}
                size="small"
            />
        );
    };

    const uploadTypeLabel = UPLOAD_TYPE_LABELS[selectedUploadType] || 'Locations';

    return (
        <div className={classes.pageContainer}>
            {/* Header */}
            <Box display="flex" justifyContent="space-between" alignItems="center" marginBottom="20px">
                <Box>
                    <Typography variant="h5" className={classes.pageTitle}>
                        BULK UPLOAD
                    </Typography>
                </Box>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<img src={bulkUploadIcon} alt="Bulk Upload" className={classes.bulkUploadIcon} />}
                    onClick={handleOpenTypeSelection}
                    className={classes.bulkUploadButton}
                >
                    Bulk upload
                </Button>
            </Box>

            {/* Filter Bar */}
            <Paper square className={classes.filterBar}>
                <Box className={classes.filterGridContainer}>
                    <FormControl variant="outlined" size="small" className={`${classes.filterControl} ${classes.filterItem}`}>
                        <InputLabel>Status</InputLabel>
                        <Select
                            IconComponent={KeyboardArrowDownIcon}
                            value={filters.status}
                            onChange={(e) => handleFilterChange('status', e.target.value)}
                            label="Status"
                        >
                            <MenuItem value="">All</MenuItem>
                            {filterOptions.statuses.map(status => (
                                <MenuItem key={status} value={status}>{status}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl variant="outlined" size="small" className={`${classes.filterControl} ${classes.filterItem}`}>
                        <InputLabel>Type</InputLabel>
                        <Select
                            IconComponent={KeyboardArrowDownIcon}
                            value={filters.type}
                            onChange={(e) => handleFilterChange('type', e.target.value)}
                            label="Type"
                        >
                            <MenuItem value="">All</MenuItem>
                            {filterOptions.types.map(type => (
                                <MenuItem key={type} value={type}>{type}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl variant="outlined" size="small" className={`${classes.filterControl} ${classes.filterItem}`}>
                        <InputLabel>Namespace</InputLabel>
                        <Select
                            IconComponent={KeyboardArrowDownIcon}
                            value={filters.namespace}
                            onChange={(e) => handleFilterChange('namespace', e.target.value)}
                            label="Namespace"
                        >
                            <MenuItem value="">All</MenuItem>
                            {filterOptions.namespaces.map(namespace => (
                                <MenuItem key={namespace} value={namespace}>{namespace}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <Box display="flex" className={classes.filterButtonContainer}>
                        <Button
                            variant="text"
                            size="small"
                            onClick={handleClearFilters}
                            className={classes.clearAllButton}
                        >
                            Clear All
                        </Button>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={handleApplyFilters}
                            className={classes.applyButton}
                        >
                            Apply
                        </Button>
                    </Box>
                </Box>
            </Paper>

            {/* Table */}
            <Paper square className={classes.tablePaper}>
                {loading && (
                    <Box display="flex" justifyContent="center" padding={4}>
                        <CircularProgress />
                    </Box>
                )}

                {!loading && (
                    <>
                        <div className={classes.tableContainer}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell className={classes.tableHeaderCell}>File Name</TableCell>
                                        <TableCell className={classes.tableHeaderCell}>
                                            <TableSortLabel
                                                active={orderBy === 'uploadedDate'}
                                                direction={orderBy === 'uploadedDate' ? order : 'asc'}
                                                onClick={() => handleRequestSort('uploadedDate')}
                                                IconComponent={(props) => {
                                                    const isActive = orderBy === 'uploadedDate';
                                                    const Icon = isActive && order === 'asc' ? ArrowUpwardIcon : ArrowDownwardIcon;
                                                    return (
                                                        <Icon
                                                            {...props}
                                                            className={isActive ? classes.sortIconActive : classes.sortIcon}
                                                        />
                                                    );
                                                }}
                                            >
                                                Uploaded Date
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell className={classes.tableHeaderCell}>Upload ID</TableCell>
                                        <TableCell className={classes.tableHeaderCell}>Uploaded By</TableCell>
                                        <TableCell className={classes.tableHeaderCell}>Type</TableCell>
                                        <TableCell align="center" className={classes.tableHeaderCell}>In Review</TableCell>
                                        <TableCell align="center" className={classes.tableHeaderCell}>Accepted</TableCell>
                                        <TableCell align="center" className={classes.tableHeaderCell}>Rejected</TableCell>
                                        <TableCell align="center" className={classes.tableHeaderCell}>Updated</TableCell>
                                        <TableCell className={classes.tableHeaderCell}>Reviewer</TableCell>
                                        <TableCell className={classes.tableHeaderCell}>Status</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {uploads.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={11} style={{ padding: 0, border: 'none' }}>
                                                <Box className={classes.emptyStateContainer}>
                                                    <DescriptionOutlinedIcon className={classes.emptyStateIcon} />
                                                    <Typography className={classes.emptyStateText}>
                                                        {(appliedFilters.status || appliedFilters.type || appliedFilters.namespace)
                                                            ? 'No bulk uploads found for this search.'
                                                            : 'No bulk uploads found for this search.'}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        uploads.map((upload) => (
                                            <TableRow key={upload.uploadId} hover>
                                                <TableCell>
                                                    <Typography
                                                        variant="body2"
                                                        className={classes.fileNameLink}
                                                    >
                                                        {upload.fileName}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>{formatDate(upload.uploadedDate)}</TableCell>
                                                <TableCell>{upload.uploadId}</TableCell>
                                                <TableCell>{upload.uploadedBy}</TableCell>
                                                <TableCell>{getTypeChip(upload.type)}</TableCell>
                                                <TableCell align="center">{upload.anomalyCount}</TableCell>
                                                <TableCell align="center">{upload.insertedCount}</TableCell>
                                                <TableCell align="center">{upload.rejectedCount}</TableCell>
                                                <TableCell align="center">{upload.updatedCount}</TableCell>
                                                <TableCell align='center'>{upload.reviewer || '--'}</TableCell>
                                                <TableCell>
                                                    {getStatusChip(upload.status)}
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </div>

                        {/* Pagination */}
                        {uploads.length > 0 && (
                            <PaginationComponent
                                queryCount={queryCount}
                                page={page}
                                handleChangePage={handleChangePage}
                                rowsPerPage={rowsPerPage}
                                handleChangeRowsPerPage={handleChangeRowsPerPage}
                            />
                        )}
                    </>
                )}
            </Paper>

            {/* Upload Modal */}
            <BulkUploadPanel
                open={uploadPanelOpen}
                onClose={() => setUploadPanelOpen(false)}
                onUploadSuccess={handleUploadSuccess}
                uploadType={selectedUploadType}
                onUploadTypeChange={handleModalTypeChange}
                initialNamespace={selectedNamespace}
            />
            <BulkUploadTypeDialog
                open={typeSelectionOpen}
                initialType={selectedUploadType}
                initialNamespace={selectedNamespace}
                onClose={handleCloseTypeSelection}
                onConfirm={handleConfirmTypeSelection}
            />
        </div>
    );
};

export default BulkUploadManagementPage;

